import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { getDashboardData, requestToJoinGroup } from '../services/api';
import Loader from '../components/common/Loader';
import Button from '../components/common/Button';
import SimpleTripPopup from '../components/common/SimpleTripPopup';
import SimpleFooter from '../components/layout/SimpleFooter';
import { 
  FaMapMarkerAlt, 
  FaCompass, 
  FaRoute, 
  FaLocationArrow,
  FaUsers,
  FaCalendarAlt,
  FaWallet,
  FaSearch,
  FaFilter,
  FaStar,
  FaFire,
  FaUserFriends,
  FaGlobeAmericas,
  FaCloud,
  FaMountain,
  FaUmbrellaBeach
} from 'react-icons/fa';
import { 
  AiOutlineUserAdd,
  AiOutlineEnvironment,
  AiOutlineRocket,
  AiOutlineTeam,
  AiOutlineHeart
} from 'react-icons/ai';
import { MdTravelExplore, MdLocationPin, MdOutlineGroup, MdOutlineExplore } from 'react-icons/md';
import { HiOutlineSparkles } from 'react-icons/hi';
import AdventureBackground from "../components/layout/AdventureBackground";

const DashboardPage = () => {
  const navigate = useNavigate();
  const { token, user } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const [dashboardData, setDashboardData] = useState({
    groups: [],
  });

  /* ---------------- GET USER'S CURRENT LOCATION ---------------- */
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        
        // Reverse geocode to get location name
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=12`
          );
          
          if (response.ok) {
            const data = await response.json();
            const address = data.address;
            const city = address.city || address.town || address.village || address.county || address.state || 'Unknown Location';
            setLocationName(city);
          }
        } catch (error) {
          console.error('Reverse geocode error:', error);
          setLocationName('Your Location');
        }
        
        setLocationLoading(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationLoading(false);
        toast.error('Unable to get your location. Using default sorting.');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  /* ---------------- CALCULATE DISTANCE BETWEEN TWO POINTS ---------------- */
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return Math.round(R * c);
  };

  /* ---------------- FETCH DASHBOARD ---------------- */
  useEffect(() => {
    if (!token) navigate('/login');
    getUserLocation();
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await getDashboardData();

      setDashboardData({
        groups: data.groups || [],
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- JOIN GROUP ---------------- */
  const handleJoin = async (groupId, destination) => {
    if (!groupId) {
      toast.error('Invalid group ID');
      return;
    }

    try {
      await requestToJoinGroup(groupId);
      toast.success(`Join request sent for ${destination}!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to join group');
    }
  };

  /* ---------------- SORT GROUPS BY DISTANCE FROM USER ---------------- */
  const sortedAndFilteredGroups = useMemo(() => {
    let groups = dashboardData.groups.filter((g) =>
      g.destination.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // If user location is available, sort by proximity to starting location
    if (userLocation && groups.length > 0) {
      const groupsWithDistance = groups.map(group => {
        let distance = Infinity;
        let startingLocationName = 'Location not specified';
        
        if (group.startingLocation && group.startingLocation.coordinates) {
          const startLat = group.startingLocation.coordinates.lat;
          const startLng = group.startingLocation.coordinates.lng;
          distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            startLat,
            startLng
          );
          startingLocationName = group.startingLocation.address;
        } else if (group.meetingPoint && group.meetingPoint.coordinates) {
          const startLat = group.meetingPoint.coordinates.lat;
          const startLng = group.meetingPoint.coordinates.lng;
          distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            startLat,
            startLng
          );
          startingLocationName = group.meetingPoint.location;
        } else {
          startingLocationName = group.destination.split(',')[0].trim();
        }
        
        return {
          ...group,
          distance,
          startingLocationName,
          isExactMatch: startingLocationName.toLowerCase().includes(locationName.toLowerCase()) ||
                       locationName.toLowerCase().includes(startingLocationName.toLowerCase())
        };
      });

      groupsWithDistance.sort((a, b) => {
        if (a.isExactMatch && !b.isExactMatch) return -1;
        if (!a.isExactMatch && b.isExactMatch) return 1;
        
        if (a.distance !== b.distance) {
          return a.distance - b.distance;
        }
        
        return new Date(a.startDate) - new Date(b.startDate);
      });

      return groupsWithDistance;
    }

    return groups.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  }, [dashboardData.groups, searchTerm, userLocation, locationName]);

  /* ---------------- LOADER ---------------- */
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full flex items-center justify-center shadow-lg"
          >
            <HiOutlineSparkles className="text-white text-3xl" />
          </motion.div>
          <p className="text-slate-700 font-medium text-lg">Loading your adventure dashboard...</p>
          <div className="mt-4 w-48 h-1 bg-slate-200 rounded-full overflow-hidden mx-auto">
            <motion.div 
              className="h-full bg-gradient-to-r from-cyan-400 to-blue-400"
              animate={{ x: [-48, 48] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdventureBackground footer={<SimpleFooter />}>
      <SimpleTripPopup />
      <div className="pt-4 sm:pt-8 md:pt-12 lg:pt-16 px-3 sm:px-4 max-w-7xl mx-auto pb-4 sm:pb-8">
        {/* Modern Gradient Orbs - Light Theme */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-gradient-to-r from-cyan-200/30 via-blue-200/30 to-purple-200/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-gradient-to-r from-purple-200/30 via-pink-200/30 to-rose-200/30 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="absolute top-1/2 left-1/4 w-60 h-60 bg-gradient-to-r from-emerald-200/20 to-teal-200/20 rounded-full blur-3xl animate-pulse delay-500" />
        </div>

        {/* Main Content */}
        <div className="relative z-10">
          {/* Header Section */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 sm:mb-8"
          >
            {/* Welcome Card */}
            <div className="bg-white/80 backdrop-blur-md rounded-2xl sm:rounded-3xl border border-slate-200/70 shadow-xl p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <motion.div 
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="relative p-2 sm:p-3 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-xl sm:rounded-2xl shadow-lg"
                  >
                    <HiOutlineSparkles className="text-white text-xl sm:text-2xl" />
                    <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-xl sm:rounded-2xl blur-sm -z-10" />
                  </motion.div>
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">
                      Welcome back, <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">{user?.name}</span>!
                    </h1>
                    <p className="text-slate-600 text-sm sm:text-base mt-1">Ready for your next adventure? ✨</p>
                  </div>
                </div>

                {/* Search Bar */}
                <div className="relative">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="relative"
                  >
                    <input
                      type="text"
                      placeholder="🔍 Search destinations, groups..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 sm:pl-12 pr-8 sm:pr-10 py-3 rounded-xl bg-white/90 backdrop-blur-sm border border-slate-300/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500 text-slate-800 placeholder-slate-500 text-sm sm:text-base transition-all duration-300 shadow-sm"
                    />
                    <FaSearch className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-cyan-500 text-base" />
                    {searchTerm && (
                      <motion.button
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 p-1"
                      >
                        ✕
                      </motion.button>
                    )}
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Location & Stats Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Location Card */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="lg:col-span-2 bg-white/80 backdrop-blur-md rounded-2xl sm:rounded-3xl border border-slate-200/70 shadow-xl p-4 sm:p-6"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <motion.div 
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        className="p-3 sm:p-4 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-xl sm:rounded-2xl text-white shadow-lg"
                      >
                        {locationLoading ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                        ) : (
                          <FaCompass className="text-lg sm:text-2xl" />
                        )}
                      </motion.div>
                      <motion.div 
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 text-base sm:text-lg mb-1">Your Location</h3>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        {locationLoading ? (
                          <div className="flex items-center gap-2 text-slate-600">
                            <div className="h-2 w-2 bg-cyan-500 rounded-full animate-pulse" />
                            <span className="text-sm">Detecting location...</span>
                          </div>
                        ) : locationName ? (
                          <>
                            <div className="flex items-center gap-2 bg-slate-100/50 px-3 py-1.5 rounded-lg">
                              <FaMapMarkerAlt className="text-red-500" />
                              <span className="font-medium text-slate-800 text-sm sm:text-base">{locationName}</span>
                            </div>
                            <span className="text-xs bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-600 px-2 py-1 rounded-lg font-medium">
                              {userLocation?.lat?.toFixed(4)}, {userLocation?.lng?.toFixed(4)}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm text-amber-600 bg-amber-100 px-3 py-1.5 rounded-lg">Enable location</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={getUserLocation}
                      disabled={locationLoading}
                      className="px-4 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg flex items-center gap-2 font-medium shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      <FaLocationArrow />
                      {locationLoading ? 'Updating...' : 'Refresh'}
                    </motion.button>
                  </div>
                </div>
                
                {/* Location Status */}
                {userLocation && sortedAndFilteredGroups.length > 0 && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-4 p-3 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl"
                  >
                    <p className="text-sm text-cyan-600">
                      📍 Showing trips starting from your location first, then nearby locations
                    </p>
                  </motion.div>
                )}
              </motion.div>

              {/* Quick Stats */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/80 backdrop-blur-md rounded-2xl sm:rounded-3xl border border-slate-200/70 shadow-xl p-4 sm:p-6"
              >
                <h3 className="text-slate-800 font-medium mb-4 flex items-center gap-2">
                  <HiOutlineSparkles className="text-cyan-500" />
                  Quick Stats
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-slate-100/50 rounded-xl">
                    <div className="text-2xl font-bold text-slate-800">{sortedAndFilteredGroups.length}</div>
                    <div className="text-xs text-slate-600 mt-1">Active Trips</div>
                  </div>
                  <div className="text-center p-3 bg-slate-100/50 rounded-xl">
                    <div className="text-2xl font-bold text-slate-800">
                      {sortedAndFilteredGroups.filter(g => g.distance < 100).length}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">Near You</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-6 sm:mb-8"
          >
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-sm rounded-lg border border-cyan-500/30">
                  <AiOutlineRocket className="text-cyan-600" />
                </div>
                Quick Actions
              </h2>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {[
                { 
                  title: 'Create Trip', 
                  description: 'Start new journey',
                  icon: <AiOutlineRocket className="text-xl" />, 
                  path: '/create-trip', 
                  gradient: 'from-cyan-500 to-blue-500',
                  hover: 'from-cyan-600 to-blue-600'
                },
                { 
                  title: 'My Groups', 
                  description: 'Manage your trips',
                  icon: <FaUsers className="text-lg" />, 
                  path: '/my-groups', 
                  gradient: 'from-emerald-500 to-teal-500',
                  hover: 'from-emerald-600 to-teal-600'
                },
                { 
                  title: 'Requests', 
                  description: 'View join requests',
                  icon: <AiOutlineUserAdd className="text-xl" />, 
                  path: '/group-requests', 
                  gradient: 'from-amber-500 to-orange-500',
                  hover: 'from-amber-600 to-orange-600'
                },
                { 
                  title: 'Explore', 
                  description: 'Discover more',
                  icon: <MdOutlineExplore className="text-xl" />, 
                  path: '/groups', 
                  gradient: 'from-purple-500 to-pink-500',
                  hover: 'from-purple-600 to-pink-600'
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -4, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(item.path)}
                  className="group cursor-pointer"
                >
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200/50 p-4 hover:border-cyan-500/30 transition-all duration-300 h-full flex flex-col shadow-lg hover:shadow-xl">
                    <div className="mb-3">
                      <motion.div 
                        whileHover={{ rotate: 15, scale: 1.1 }}
                        className={`w-12 h-12 rounded-lg bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white shadow-lg mb-3`}
                      >
                        {item.icon}
                      </motion.div>
                      <h3 className="font-bold text-slate-800 text-base mb-1">{item.title}</h3>
                      <p className="text-slate-600 text-sm">{item.description}</p>
                    </div>
                    <motion.div 
                      className="mt-auto flex items-center text-sm font-medium text-cyan-600 group-hover:text-cyan-500"
                      whileHover={{ x: 5 }}
                    >
                      <span>Get started</span>
                      <span className="ml-2">→</span>
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Active Groups Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-6 sm:mb-8"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-sm rounded-lg border border-purple-500/30">
                  <FaGlobeAmericas className="text-purple-600 text-lg" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Active Groups</h2>
                  <p className="text-slate-600 text-sm sm:text-base">
                    {sortedAndFilteredGroups.length} trips • {userLocation ? 'Sorted by distance' : 'All trips'}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <div className="flex items-center gap-2 px-3 py-2 bg-white/80 backdrop-blur-sm rounded-lg border border-slate-300/50 shadow-sm">
                  <FaFilter className="text-cyan-600" />
                  <select 
                    value={activeFilter}
                    onChange={(e) => setActiveFilter(e.target.value)}
                    className="bg-transparent border-none focus:outline-none text-slate-800 text-sm"
                  >
                    <option value="all">All Trips</option>
                    <option value="nearby">Near Me</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="popular">Popular</option>
                  </select>
                </div>
              </div>
            </div>

            <AnimatePresence>
              {sortedAndFilteredGroups.length === 0 ? (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-white/80 backdrop-blur-md rounded-2xl sm:rounded-3xl border border-slate-200/70 p-6 sm:p-8 text-center shadow-xl"
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full flex items-center justify-center border border-cyan-500/30">
                    <MdTravelExplore className="text-cyan-600 text-2xl" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-2">No trips found</h3>
                  <p className="text-slate-600 text-sm sm:text-base mb-4 max-w-md mx-auto">
                    {searchTerm ? `No destinations matching "${searchTerm}"` : 'No active groups available'}
                  </p>
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/create-trip')}
                    className="px-4 sm:px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 text-sm sm:text-base"
                  >
                    Create Your First Trip
                  </motion.button>
                </motion.div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <AnimatePresence>
                    {sortedAndFilteredGroups.map((g, index) => {
                      const isMember = g.currentMembers?.some(
                        m => m.user && m.user._id === user?._id
                      );
                      const isCreator = g.createdBy?.id === user?._id;
                      
                      let locationBadge = null;
                      if (g.isExactMatch) {
                        locationBadge = { 
                          text: 'Starts here', 
                          gradient: 'from-emerald-500 to-green-500',
                          icon: '📍'
                        };
                      } else if (g.distance < 50) {
                        locationBadge = { 
                          text: 'Very close', 
                          gradient: 'from-cyan-500 to-blue-500',
                          icon: '🚗'
                        };
                      } else if (g.distance < 200) {
                        locationBadge = { 
                          text: 'Same region', 
                          gradient: 'from-amber-500 to-orange-500',
                          icon: '🗺️'
                        };
                      }

                      return (
                        <motion.div
                          key={g.id}
                          initial={{ opacity: 0, y: 30, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ delay: index * 0.05 }}
                          whileHover={{ y: -4, transition: { duration: 0.2 } }}
                          onClick={() => navigate(`/groups/${g.id}`)}
                          className="group cursor-pointer"
                        >
                          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/70 overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                            {/* Card Header */}
                            <div className="relative p-4 sm:p-6">
                              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-blue-500/5 to-purple-500/5" />
                              <div className="relative">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-lg sm:text-xl font-bold text-slate-800 group-hover:text-cyan-700 transition-colors truncate">
                                      {g.destination}
                                    </h3>
                                    <div className="flex items-center gap-2 mt-2 text-slate-600 text-sm">
                                      <FaCalendarAlt className="text-cyan-600" />
                                      <span>
                                        {new Date(g.startDate).toLocaleDateString()} – {new Date(g.endDate).toLocaleDateString()}
                                      </span>
                                    </div>
                                  </div>
                                  {isCreator && (
                                    <span className="px-2 py-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-700 text-xs font-medium rounded-lg border border-purple-500/30">
                                      Your Group
                                    </span>
                                  )}
                                </div>

                                {/* Location Badge */}
                                {locationBadge && (
                                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r ${locationBadge.gradient} text-white text-xs font-medium shadow-lg`}>
                                    <span>{locationBadge.icon}</span>
                                    <span>{locationBadge.text}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Card Body */}
                            <div className="p-4 sm:p-6 pt-0 flex-1">
                              {/* Starting Location */}
                              <div className="mb-4 p-3 bg-slate-100/50 rounded-xl border border-slate-200/50">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="p-2 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-lg">
                                    <MdLocationPin className="text-cyan-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-slate-600">Starting Point</p>
                                    <p className="text-slate-800 font-medium truncate">{g.startingLocationName}</p>
                                  </div>
                                </div>
                                {userLocation && g.distance !== Infinity && (
                                  <div className="flex items-center justify-between pt-3 border-t border-slate-200/50">
                                    <div className="flex items-center gap-2 text-xs text-slate-600">
                                      <FaRoute className="text-cyan-600" />
                                      <span>{g.distance} km away</span>
                                    </div>
                                    {g.distance < 100 && (
                                      <span className="text-xs text-emerald-600">
                                        ⏱️ ~{Math.round(g.distance / 60)}h
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Description */}
                              <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                                {g.description || "No description provided."}
                              </p>

                              {/* Stats */}
                              <div className="grid grid-cols-2 gap-3 mb-4">
                                <div className="p-3 bg-slate-100/50 rounded-lg border border-slate-200/50">
                                  <p className="text-xs text-slate-600 mb-1">Budget</p>
                                  <p className="text-slate-800 font-medium">
                                    ₹{g.budget?.min || 0} – ₹{g.budget?.max || 0}
                                  </p>
                                </div>
                                <div className="p-3 bg-slate-100/50 rounded-lg border border-slate-200/50">
                                  <p className="text-xs text-slate-600 mb-1">Organizer</p>
                                  <p className="text-slate-800 font-medium truncate">
                                    {g.createdBy?.name || "Unknown"}
                                  </p>
                                </div>
                              </div>

                              {/* Members Progress */}
                              <div className="mb-4">
                                <div className="flex justify-between text-xs text-slate-600 mb-2">
                                  <span>Members</span>
                                  <span>
                                    {g.currentMembersCount || 0}/{g.maxMembers || 10}
                                  </span>
                                </div>
                                <div className="h-1.5 bg-slate-200/50 rounded-full overflow-hidden">
                                  <motion.div 
                                    initial={{ width: 0 }}
                                    animate={{ 
                                      width: `${((g.currentMembersCount || 0) / (g.maxMembers || 10)) * 100}%` 
                                    }}
                                    transition={{ duration: 1, delay: 0.5 }}
                                    className="h-full bg-gradient-to-r from-cyan-500 to-blue-500"
                                  />
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  onClick={() => navigate(`/groups/${g.id}`)}
                                  className="flex-1 px-3 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg text-sm transition-colors"
                                  size="sm"
                                >
                                  View Details
                                </Button>

                                {isCreator ? (
                                  <Button
                                    onClick={() => navigate(`/groups/${g.id}`)}
                                    className="flex-1 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg text-sm"
                                    size="sm"
                                  >
                                    Manage
                                  </Button>
                                ) : isMember ? (
                                  <Button
                                    onClick={() => navigate(`/groups/${g.id}`)}
                                    className="flex-1 px-3 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-lg text-sm"
                                    size="sm"
                                  >
                                    Enter Group
                                  </Button>
                                ) : g.currentMembersCount >= g.maxMembers ? (
                                  <Button 
                                    disabled 
                                    className="flex-1 px-3 py-2 bg-slate-100 text-slate-500 rounded-lg text-sm"
                                    size="sm"
                                  >
                                    Group Full
                                  </Button>
                                ) : (
                                  <Button
                                    onClick={() => handleJoin(g.id, g.destination)}
                                    className="flex-1 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg text-sm"
                                    size="sm"
                                  >
                                    Join Trip
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              )}
            </AnimatePresence>
          </motion.section>

          {/* Find Travel Buddies Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-6 sm:mb-8"
          >
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="p-1.5 bg-gradient-to-r from-rose-500/20 to-pink-500/20 backdrop-blur-sm rounded-lg border border-rose-500/30">
                <AiOutlineTeam className="text-rose-600" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Find Travel Buddies</h2>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              {/* Suggested Friends */}
              <motion.div 
                whileHover={{ y: -4 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/70 p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2.5 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-lg mr-3">
                    <AiOutlineUserAdd className="text-cyan-600 text-lg" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-base">Suggested Friends</h3>
                    <p className="text-slate-600 text-sm">Based on your interests</p>
                  </div>
                </div>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/find-friends')}
                  className="w-full mt-2 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 text-sm"
                >
                  Discover Buddies
                </motion.button>
              </motion.div>

              {/* Nearby Travelers */}
              <motion.div 
                whileHover={{ y: -4 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/70 p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2.5 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-lg mr-3">
                    <FaMapMarkerAlt className="text-emerald-600 text-lg" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-base">Nearby Travelers</h3>
                    <p className="text-slate-600 text-sm">Find people in your area</p>
                  </div>
                </div>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/nearby-users')}
                  className="w-full mt-2 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 text-sm"
                >
                  Explore Nearby
                </motion.button>
              </motion.div>

              {/* Similar Interests */}
              <motion.div 
                whileHover={{ y: -4 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/70 p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg mr-3">
                    <MdTravelExplore className="text-purple-600 text-lg" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-base">Similar Interests</h3>
                    <p className="text-slate-600 text-sm">Match with travelers</p>
                  </div>
                </div>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/match-travelers')}
                  className="w-full mt-2 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 text-sm"
                >
                  Find Matches
                </motion.button>
              </motion.div>
            </div>
          </motion.div>

          {/* Stats Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="bg-white/80 backdrop-blur-md rounded-2xl sm:rounded-3xl border border-slate-200/70 p-4 sm:p-6 shadow-xl"
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
              {[
                { 
                  count: sortedAndFilteredGroups.length, 
                  label: 'Total Trips', 
                  icon: '✈️',
                  color: 'text-cyan-600'
                },
                { 
                  count: sortedAndFilteredGroups.filter(g => g.distance < 100).length, 
                  label: 'Near You', 
                  icon: '📍',
                  color: 'text-emerald-600'
                },
                { 
                  count: sortedAndFilteredGroups.filter(g => g.isExactMatch).length, 
                  label: 'In Your City', 
                  icon: '🏙️',
                  color: 'text-purple-600'
                },
                { 
                  count: sortedAndFilteredGroups.filter(g => g.currentMembersCount >= g.maxMembers).length, 
                  label: 'Popular', 
                  icon: '🔥',
                  color: 'text-rose-600'
                },
              ].map((stat, index) => (
                <motion.div 
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  className="text-center p-3 sm:p-4 bg-slate-100/50 rounded-xl border border-slate-200/50"
                >
                  <div className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">
                    {stat.count}
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-sm">{stat.icon}</span>
                    <div className="text-xs text-slate-600">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Floating Action Button */}
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.1, rotate: 5 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => navigate('/create-trip')}
          className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 p-3 sm:p-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 z-50"
        >
          <AiOutlineRocket className="text-lg sm:text-xl" />
        </motion.button>

        {/* Custom Light Theme Styles */}
        <style jsx="true">{`
          /* Light theme enhancements */
          @keyframes gentleFloat {
            0%, 100% { 
              transform: translate(0, 0) rotate(0deg) scale(1);
            }
            33% { 
              transform: translate(15px, -20px) rotate(3deg) scale(1.03);
            }
            66% { 
              transform: translate(-10px, 10px) rotate(-2deg) scale(0.98);
            }
          }

          .group:hover .group-hover\\:animate-gentleFloat {
            animation: gentleFloat 3s ease-in-out infinite;
          }

          /* Gradient text animation */
          @keyframes gradientShift {
            0%, 100% { 
              background-position: 0% 50%;
            }
            50% { 
              background-position: 100% 50%;
            }
          }

          .animate-gradient-shift {
            background-size: 200% auto;
            animation: gradientShift 3s ease-in-out infinite;
          }

          /* Card hover glow effect */
          .glow-on-hover {
            position: relative;
            transition: all 0.3s ease;
          }

          .glow-on-hover::before {
            content: '';
            position: absolute;
            inset: -2px;
            background: linear-gradient(45deg, 
              rgba(6, 182, 212, 0.3), 
              rgba(59, 130, 246, 0.3), 
              rgba(168, 85, 247, 0.3));
            border-radius: inherit;
            z-index: -1;
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          .glow-on-hover:hover::before {
            opacity: 1;
          }

          /* Smooth transitions for mobile */
          @media (max-width: 640px) {
            * {
              transition: all 0.2s ease;
            }
          }

          /* Better scrollbar for light theme */
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          ::-webkit-scrollbar-track {
            background: rgba(241, 245, 249, 0.5);
            border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb {
            background: linear-gradient(45deg, #06b6d4, #3b82f6);
            border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(45deg, #0891b2, #2563eb);
          }
        `}</style>
      </div>
    </AdventureBackground>
  );
};

export default DashboardPage;